/**
 * EscapadaDetail — editorial full page for a single low-cost escapada.
 * URL: /escapadas/:slug
 *
 * Reads content from /src/data/escapadas.js and renders it with premium
 * editorial styling matching the rest of the Escapafy brand.
 */
import { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  Plane,
  Clock,
  Users,
  Star,
  Bed,
  Ticket,
  ShieldCheck,
} from "lucide-react";
import { ESCAPADAS } from "../data/escapadas";
import { publicEscapadasApi } from "../admin/api";
import { LOGO_URL } from "../data/escapafy";

const ICON_MAP = { plane: Plane, bed: Bed, ticket: Ticket, shield: ShieldCheck };

export default function EscapadaDetail() {
  const { slug } = useParams();
  const staticEscapada = useMemo(
    () => ESCAPADAS.find((e) => e.slug === slug),
    [slug],
  );
  const [escapada, setEscapada] = useState(staticEscapada || null);
  const [notFound, setNotFound] = useState(false);

  // Scroll to top when navigating to a new escapada
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    if (staticEscapada) { setEscapada(staticEscapada); return; }
    // Fallback: try DB
    publicEscapadasApi.get(slug)
      .then((d) => setEscapada(d))
      .catch(() => setNotFound(true));
  }, [slug, staticEscapada]);

  if (notFound || (!escapada && !staticEscapada)) {
    if (notFound) {
      return (
        <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
          <SimpleHeader />
          <div className="flex-1 flex items-center justify-center px-6 text-center">
            <div>
              <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-3">Escapada no encontrada</p>
              <h1 className="font-serif italic text-3xl text-[#0A192F]">Ups, esta escapada no existe todavía.</h1>
              <Link to="/business#escapadas" className="mt-8 inline-flex items-center gap-2 text-[#0A192F] underline underline-offset-4 text-sm">
                <ArrowLeft size={14} /> Volver a las escapadas
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-[#3A506B] italic">Cargando…</p>
      </div>
    );
  }

  const { title, tagline, hero_image, summary, intro, days, tips, opinion, affiliates, gallery, content } = escapada;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="escapada-detail">
      <SimpleHeader />

      {/* Hero — split layout to respect portrait photo */}
      <section className="relative w-full bg-[#0A192F] text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[62vh] lg:min-h-[76vh]">
          {/* Left: text */}
          <div className="relative flex items-center px-6 lg:px-14 py-16 lg:py-20 order-2 lg:order-1">
            <div aria-hidden className="absolute -right-16 -top-8 h-56 w-56 rounded-full border border-[#E8C97A]/15" />
            <div className="relative max-w-xl">
              <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-4" data-testid="escapada-eyebrow">Escapadas Low Cost</p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="escapada-title">
                {title}
              </h1>
              <p className="mt-6 text-white/85 text-base lg:text-lg leading-relaxed">{tagline}</p>
            </div>
          </div>
          {/* Right: photo (portrait-friendly, no crop) */}
          <div className="relative order-1 lg:order-2 h-[52vh] lg:h-auto bg-[#0A192F]">
            <img
              src={hero_image}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center center" }}
            />
            {/* subtle left-fade so title breathes at md junction */}
            <div className="hidden lg:block absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0A192F] to-transparent" />
          </div>
        </div>
      </section>

      {/* Summary card */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 mt-12 lg:mt-16 relative z-10">
        <div className="bg-white border border-[#EAE3D5] p-6 lg:p-8 shadow-[0_20px_60px_rgba(10,25,47,0.08)] grid grid-cols-2 md:grid-cols-5 gap-6" style={{ borderRadius: "4px" }}>
          <SummaryItem Icon={MapPin} label="Destino" value={summary.destination} />
          <SummaryItem Icon={Plane} label="Vuelo" value={summary.flight} />
          <SummaryItem Icon={Clock} label="Duración" value={summary.duration} />
          <SummaryItem Icon={Users} label="Ideal para" value={summary.ideal_for} />
          <SummaryItem Icon={Star} label="Lo más especial" value={summary.highlight} />
        </div>
      </section>

      {/* Editorial content */}
      <article className="max-w-3xl mx-auto px-6 lg:px-0 py-16 lg:py-20 text-[#0A192F]">
        {intro && (
          <div className="prose-editorial font-serif text-lg lg:text-xl leading-relaxed text-[#0A192F]/90 mb-12 whitespace-pre-line">
            {intro}
          </div>
        )}

        {(days || []).map((d, idx) => (
          <section key={idx} className="mb-14" data-testid={`day-section-${idx}`}>
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight text-[#0A192F] leading-tight">
              {d.title}
            </h2>
            <div className="mt-5 space-y-4 text-[#3A506B] text-base lg:text-lg leading-relaxed">
              {d.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {d.inline_cta && (
              <div className="mt-6">
                <a
                  href={d.inline_cta.href}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="inline-flex items-center gap-2 border-l-2 border-[#E8C97A] pl-4 py-2 text-[#0A192F] hover:text-[#16335A] text-sm lg:text-base font-medium underline underline-offset-4 decoration-[#E8C97A] hover:decoration-[#0A192F] transition-colors"
                  data-testid={`day-cta-${idx}`}
                >
                  👉 {d.inline_cta.label} <ArrowUpRight size={14} />
                </a>
              </div>
            )}
            {d.outro && (
              <p className="mt-5 text-[#3A506B] text-base lg:text-lg leading-relaxed italic">{d.outro}</p>
            )}
          </section>
        ))}

        {/* Markdown-lite content (only when no structured `days`) */}
        {(!days || days.length === 0) && content && <MarkdownLite text={content} />}

        {/* Tips */}
        {tips?.length > 0 && (
          <section className="mt-16 bg-[#F3EFE7] border border-[#EAE3D5] p-8 lg:p-10" style={{ borderRadius: "4px" }}>
            <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-3">Consejos</p>
            <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F]">Lo que haríamos diferente</h3>
            <ul className="mt-6 space-y-3 text-[#3A506B] text-base leading-relaxed">
              {tips.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-[#E8C97A] mt-1.5 shrink-0">✦</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Opinion */}
        {opinion && (
          <section className="mt-16">
            <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-3">Nuestra opinión</p>
            <blockquote className="font-serif italic text-xl lg:text-2xl text-[#0A192F] leading-relaxed border-l-2 border-[#E8C97A] pl-6">
              {opinion}
            </blockquote>
          </section>
        )}
      </article>

      {/* Gallery */}
      {gallery?.length > 0 && (
        <section className="bg-[#F3EFE7] py-16 lg:py-20" data-testid="escapada-gallery-section">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-3">Postales del viaje</p>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A192F] tracking-tight leading-tight max-w-2xl">
              Un recuerdo de{" "}
              <span className="italic text-[#16335A]">cada rincón.</span>
            </h2>
            <ul
              data-testid="escapada-gallery-grid"
              className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}
            >
              {gallery.map((g, i) => (
                <li
                  key={i}
                  className="group relative overflow-hidden bg-[#EAE3D5] aspect-[3/4]"
                  style={{ borderRadius: "3px" }}
                >
                  <img
                    src={g.src}
                    alt={g.caption || ""}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Planifica tu escapada — affiliate CTAs */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-3">Planifica tu escapada</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight">Reserva cada elemento directamente con el proveedor oficial.</h2>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="escapada-affiliate-grid">
            {affiliates.map((a, i) => {
              const Icon = ICON_MAP[a.icon] || Plane;
              return (
                <li key={i}>
                  <a
                    href={a.href}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid={`escapada-affiliate-${a.provider.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex flex-col justify-between h-full bg-white/[0.04] border border-white/10 hover:bg-white hover:text-[#0A192F] hover:border-transparent p-6 transition-all duration-300"
                    style={{ borderRadius: "2px" }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="h-10 w-10 rounded-full bg-[#E8C97A]/15 text-[#E8C97A] group-hover:bg-[#0A192F] group-hover:text-[#E8C97A] flex items-center justify-center transition-colors">
                        <Icon size={18} />
                      </div>
                      <ArrowUpRight size={16} className="opacity-60 group-hover:opacity-100 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                    <div>
                      <p className="font-display text-lg lg:text-xl leading-tight">{a.label}</p>
                      <p className="text-xs tracking-[0.2em] uppercase mt-2 text-white/60 group-hover:text-[#3A506B]">{a.provider}</p>
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>
          <p className="mt-8 text-white/50 text-xs italic max-w-2xl leading-relaxed">
            Algunos enlaces son de afiliados. Si reservas a través de ellos, el precio para ti no cambia y nos ayudas a
            seguir creando nuevas escapadas reales. ¡Muchas gracias!
          </p>
        </div>
      </section>
    </div>
  );
}

// ── Sub-components ──
function SimpleHeader() {
  return (
    <header className="border-b border-[#EAE3D5] bg-[#FDFBF7]/95 backdrop-blur sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/business" className="text-[#3A506B] hover:text-[#0A192F] text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 transition-colors" data-testid="back-to-landing">
          <ArrowLeft size={14} /> Escapafy Business
        </Link>
        <Link to="/business">
          <img src={LOGO_URL} alt="Escapafy" style={{ height: "68px", width: "auto" }} />
        </Link>
      </div>
    </header>
  );
}

function SummaryItem({ Icon, label, value }) {
  return (
    <div className="flex flex-col">
      <div className="h-9 w-9 rounded-full bg-[#E8C97A]/15 text-[#E8C97A] flex items-center justify-center mb-3">
        <Icon size={16} />
      </div>
      <p className="text-[#3A506B] text-[0.65rem] tracking-[0.25em] uppercase mb-1">{label}</p>
      <p className="text-[#0A192F] text-sm leading-snug">{value}</p>
    </div>
  );
}

// ── Markdown-lite renderer ──
// Supports:
//   ## Title                → new section (day) heading
//   >> Label | https://…    → inline CTA (opens in new tab as affiliate)
//   [text](https://…)       → inline affiliate link (opens in new tab)
//   **bold**  *italic*      → inline text formatting
//   Empty lines split paragraphs
function renderInline(text) {
  // Permissive markdown-lite:
  //   [label](url)  → link. URL may contain any char except ')', including
  //                   accidental whitespace/newlines from copy-paste (we strip).
  //   **bold**  *italic*
  const regex = /(\[([^\]]+)\]\s*\((https?:\/\/[^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  const parts = [];
  let lastIdx = 0;
  let key = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    else if (m.index === lastIdx && parts.length > 0) {
      parts.push(" ");
    }
    if (m[2] !== undefined && m[3] !== undefined) {
      const cleanUrl = m[3].replace(/\s+/g, ""); // strip accidental whitespace/newlines
      parts.push(
        <a
          key={`a${key++}`}
          href={cleanUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="text-[#16335A] hover:text-[#0A192F] underline underline-offset-4 decoration-[#E8C97A] decoration-2 hover:decoration-[#0A192F] transition-colors"
        >
          {m[2]}
        </a>,
      );
    } else if (m[4] !== undefined) {
      parts.push(<strong key={`b${key++}`} className="text-[#0A192F] font-semibold">{m[4]}</strong>);
    } else if (m[5] !== undefined) {
      parts.push(<em key={`i${key++}`}>{m[5]}</em>);
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts.length ? parts : text;
}

function MarkdownLite({ text }) {
  const sections = [];
  let current = { title: null, blocks: [] };
  const flushSection = () => {
    if (current.title || current.blocks.length) sections.push(current);
    current = { title: null, blocks: [] };
  };
  const lines = (text || "").split("\n");
  let paragraph = [];
  const flushParagraph = () => {
    if (paragraph.length) {
      current.blocks.push({ type: "p", text: paragraph.join(" ").trim() });
      paragraph = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("## ")) {
      flushParagraph();
      flushSection();
      current.title = line.slice(3).trim();
    } else if (line.startsWith(">> ")) {
      flushParagraph();
      const body = line.slice(3);
      const [label, href] = body.split("|").map((s) => s.trim());
      current.blocks.push({ type: "cta", label, href });
    } else if (line === "") {
      flushParagraph();
    } else {
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushSection();

  return (
    <>
      {sections.map((s, i) => (
        <section key={i} className="mb-14">
          {s.title && (
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight text-[#0A192F] leading-tight">{s.title}</h2>
          )}
          <div className="mt-5 space-y-4 text-[#3A506B] text-base lg:text-lg leading-relaxed">
            {s.blocks.map((b, j) => {
              if (b.type === "p") return <p key={j}>{renderInline(b.text)}</p>;
              if (b.type === "cta") return (
                <div key={j} className="mt-2">
                  <a
                    href={b.href}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="inline-flex items-center gap-2 border-l-2 border-[#E8C97A] pl-4 py-2 text-[#0A192F] hover:text-[#16335A] text-sm lg:text-base font-medium underline underline-offset-4 decoration-[#E8C97A] hover:decoration-[#0A192F] transition-colors"
                  >
                    👉 {b.label} <ArrowUpRight size={14} />
                  </a>
                </div>
              );
              return null;
            })}
          </div>
        </section>
      ))}
    </>
  );
}
